import { faClock, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { differenceInDays, differenceInWeeks, isPast } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, HeadlineL } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import Dialog from '@sorare/core/src/components/dialog';
import { useOneTimeDialogContext } from '@sorare/core/src/contexts/oneTimeDialog';
import { OneTimeDialog } from '@sorare/core/src/contexts/oneTimeDialog/Provider';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { LIFECYCLE } from '@sorare/core/src/hooks/useLifecycle';
import {
  earlyAccessLaunchMessages,
  glossary,
} from '@sorare/core/src/lib/glossary';

import { Countdown } from '@sorare/marketplace/src/components/market/Countdown';
import { earlyAccessEvents } from '@sorare/marketplace/src/lib/earlyAccess';

import contender from './assets/contender.jpg';

const Wrapper = styled.div`
  --c-dialog-background: transparent;
`;

const BlackBackground = styled(Vertical)`
  background-color: var(--c-neutral-100);
`;
const Block = styled(Vertical)<{ $background: string }>`
  padding: var(--double-unit);
  ${({ $background }) =>
    $background
      ? css`
          background-image: url(${$background});
          background-size: cover;
          background-position: center;
        `
      : ''};
  &:last-child {
    border-bottom-left-radius: var(--unit);
    border-bottom-right-radius: var(--unit);
  }
`;
const MainBlock = styled(Block)`
  justify-content: flex-start;
  height: 340px;
`;

const Actions = styled(Vertical)`
  padding: var(--unit);
`;

const Icon = styled.img`
  width: var(--double-unit);
`;

const Shadow = styled.div`
  border-radius: var(--triple-unit);
  box-shadow: 0px 0px 20px 0px rgba(var(--c-rgb-neutral-1000), 0.25);
`;
const now = new Date();

const [contenderEvent, ...otherEvents] = earlyAccessEvents;

export const EarlyAccessAnnouncement = () => {
  const {
    flags: {
      useEnableEarlyAccessFlows = false,
      useEarlyAccessDetailsLink = '/blog',
    },
  } = useFeatureFlags();
  const { sawDialog } = useOneTimeDialogContext();

  if (
    isPast(contenderEvent.startDate) ||
    sawDialog(LIFECYCLE.sawEarlyAccessAnnouncementDialog) ||
    !useEnableEarlyAccessFlows
  )
    return null;

  return (
    <Wrapper>
      <OneTimeDialog
        dialogId={LIFECYCLE.sawEarlyAccessAnnouncementDialog}
        show={!sawDialog(LIFECYCLE.sawEarlyAccessAnnouncementDialog)}
      >
        {({ onClose, open }) => (
          <Dialog
            maxWidth="xs"
            fullWidth
            hideHeader
            transparent
            open={open}
            onClose={onClose}
            fullScreen={false}
          >
            <Vertical>
              <BlackBackground>
                <MainBlock $background={contender} center>
                  <Shadow>
                    <Chip
                      label={
                        <FormattedMessage
                          {...earlyAccessLaunchMessages.newSeason}
                        />
                      }
                      size="smaller"
                      color="opa"
                    />
                  </Shadow>
                  <HeadlineL>
                    <FormattedMessage
                      {...earlyAccessLaunchMessages.contender}
                    />
                  </HeadlineL>
                  <Countdown
                    endDate={contenderEvent.startDate}
                    unitColor="rgba(var(--c-rgb-neutral-1000), 0.6)"
                    expanded={false}
                  />
                </MainBlock>
                {otherEvents.map(e => {
                  const diffInWeeks = differenceInWeeks(e.startDate, now);
                  return (
                    <Block key={e.slug} $background={e.background} center>
                      <Horizontal>
                        <Icon src={e.icon} />
                        <BodyL>
                          <FormattedMessage {...e.launchMessage} />
                        </BodyL>
                        <Chip
                          size="smaller"
                          iconLeft={() => <FontAwesomeIcon icon={faClock} />}
                          label={
                            diffInWeeks >= 1 ? (
                              <FormattedMessage
                                id="earlyAccessAnnouncement.launchDate.weeks"
                                defaultMessage="in {weeks, plural, one {# week} other {# weeks}}"
                                values={{
                                  weeks: diffInWeeks,
                                }}
                              />
                            ) : (
                              <FormattedMessage
                                id="earlyAccessAnnouncement.launchDate.days"
                                defaultMessage="in {days, plural, one {# day} other {# days}}"
                                values={{
                                  days: differenceInDays(e.startDate, now),
                                }}
                              />
                            )
                          }
                          color="opa"
                        />
                      </Horizontal>
                    </Block>
                  );
                })}
              </BlackBackground>
              <Actions center gap={2}>
                <Button
                  fullWidth
                  color="secondary"
                  size="medium"
                  onClick={onClose}
                >
                  <FormattedMessage {...glossary.gotIt} />
                </Button>
                <Button
                  size="medium"
                  color="transparent"
                  startIcon={<FontAwesomeIcon icon={faInfoCircle} />}
                  href={useEarlyAccessDetailsLink}
                >
                  <FormattedMessage
                    id="earlyAccessAnnouncement.blogLink.label"
                    defaultMessage="More information"
                  />
                </Button>
              </Actions>
            </Vertical>
          </Dialog>
        )}
      </OneTimeDialog>
    </Wrapper>
  );
};
